// #39 Journey: a horizontal wizard of numbered tiles (*HRBP request creation · HM certification · HRBP approval*;
// *Packet Draft · Generated · Reviewed · Released* …). A tile is done (check on --t-green), active (blue ring, the
// current step), next, lock (lock glyph: not open yet) or returned (an orange tile: a returned request steps back).
// `parallel` lays the equal tiles under an *in parallel* caption (DC-64: work that fans out at once); no tile is drawn
// as waiting on another. Tiles sit in one row while they fit at 150 px and wrap after that (one column ≤ 768 px).
// A tile with href/onOpen is a jump link (F); a tile that would jump but may not is `blocked` (D: dashed,
// aria-disabled, the reason inline). `sm` is the compact tile. `by` adds the sub-line *actor · date* (who did the step and
// when, the date a <time>); a tile's `pill` carries its status, one per tile in `parallel` (B2-2).

import type { ReactNode } from "react";
import { useBlocked, type Blocked } from "../core/Button";
import { Icon, type IconName } from "../core/icons";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { formatDate, toEpoch, type Instant } from "../lib/time";
import { Tooltip } from "../lib/Tooltip";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { JOURNEY_LABELS, PROGRESS_STATE_WORDS } from "./labels";

export type JourneyState = "done" | "active" | "next" | "lock" | "returned";

export interface JourneyStep {
  /** The step's name, from a labels module. */
  label: string;
  /** The line under the name (a status word, a date). */
  sub?: ReactNode | undefined;
  /** Default next. */
  state?: JourneyState | undefined;
  /** The tile's number (default 01, 02 …). */
  n?: string | undefined;
  icon?: IconName | undefined;
  /** Who did the step and when: the sub-line *actor · date*, under `sub`. */
  by?: { actor: string; at: Instant } | undefined;
  /** A status pill on the tile (the parallel tiles carry one each). */
  pill?: ReactNode | undefined;
  href?: string | undefined;
  onOpen?: (() => void) | undefined;
  /** A jump that is not open to this seat yet: dashed, aria-disabled, the reason. */
  blocked?: Blocked | undefined;
  hs?: Hs | undefined;
}

export interface JourneyProps {
  steps: readonly JourneyStep[];
  /** The list's name. */
  label: string;
  sm?: boolean | undefined;
  parallel?: boolean | undefined;
  className?: string | undefined;
}

function glyph(step: JourneyStep, index: number, sm: boolean): ReactNode {
  const size = sm ? 14 : 16;
  if (step.state === "done") return <Icon name="check" size={size} />;
  if (step.state === "lock") return <Icon name="lock" size={14} />;
  if (step.state === "returned") return <Icon name="back" size={size} />;
  if (step.icon) return <Icon name={step.icon} size={14} />;
  return step.n ?? String(index + 1).padStart(2, "0");
}

/** The state a sighted reader takes from the glyph; `active` is aria-current, `lock` has no word yet. */
const STATE_WORD: Partial<Record<JourneyState, string>> = {
  done: PROGRESS_STATE_WORDS.completed, returned: PROGRESS_STATE_WORDS.returned,
};

function Tile({ step, index, sm }: { step: JourneyStep; index: number; sm: boolean }) {
  const hot = useHs(step.hs);
  const block = useBlocked(step.blocked);
  const state = step.state ?? "next";
  const classes = cx("mc-jstep", `mc-jstep-${state}`, (step.href !== undefined || step.onOpen) && "mc-jstep-link", step.blocked && "mc-dis");
  const body = (
    <>
      <span className="mc-jn" aria-hidden={typeof glyph(step, index, sm) === "string" ? undefined : true}>{glyph(step, index, sm)}</span>
      <span className="mc-jt">
        <b>{step.label}</b>
        {STATE_WORD[state] !== undefined && STATE_WORD[state] !== step.label ? <VisuallyHidden>{", "}{STATE_WORD[state]}</VisuallyHidden> : null}
        {step.sub !== undefined && step.sub !== null ? <span>{step.sub}</span> : null}
        {step.by ? (
          <span className="mc-jby">
            {`${step.by.actor} · `}<time dateTime={new Date(toEpoch(step.by.at)).toISOString()}>{formatDate(step.by.at)}</time>
          </span>
        ) : null}
      </span>
      {step.pill ? <span className="mc-jp">{step.pill}</span> : null}
    </>
  );
  const current = state === "active" ? ("step" as const) : undefined;
  if (step.blocked) {
    return (
      <>
        <Tooltip label={step.blocked.reason} mode="mirror">
          <span className={classes} role="link" tabIndex={0} aria-current={current} aria-describedby={block.describedBy} {...block.props} {...hot}>{body}</span>
        </Tooltip>
        {block.reason}
      </>
    );
  }
  if (step.href !== undefined) return <a className={classes} href={step.href} onClick={step.onOpen} aria-current={current} {...hot}>{body}</a>;
  if (step.onOpen) return <button type="button" className={classes} onClick={step.onOpen} aria-current={current} {...hot}>{body}</button>;
  return <span className={classes} aria-current={current}>{body}</span>;
}

export function Journey({ steps, label, sm, parallel, className }: JourneyProps) {
  const list = (
    <ol
      className={cx("mc-journey", sm && "mc-journey-sm", parallel && "mc-journey-par", !parallel && className)}
      aria-label={label}
    >
      {steps.map((step, index) => (
        <li key={`${step.label}-${index}`} className="mc-jli">
          <Tile step={step} index={index} sm={Boolean(sm)} />
        </li>
      ))}
    </ol>
  );
  if (!parallel) return list;
  return (
    <div className={cx("mc-journey-w", className)}>
      <p className="mc-journey-cap">{JOURNEY_LABELS.inParallel}</p>
      {list}
    </div>
  );
}
